import { useState, useEffect, useCallback } from 'react'
import * as pdfjsLib from 'pdfjs-dist'
import { i18n } from '@fs/zion-locale'
import { fetchFanChartPDF } from '../../services/FanChartAPI/fanChartAPI'
import useGenerationCount from '../useGenerationCount/useGenerationCount'
import './pdfWorkerSetup'

// In-memory cache for converted fan chart images (cleared on page refresh)
// Key: `${personId}_${numberOfGenerations}`, Value: data URL string
const fanChartCache = new Map()

const MIN_GENERATIONS = 4
const MAX_GENERATIONS = 6

const TRIM_THRESHOLD = 240
const TRIM_PADDING = 40

// Scans inward from each edge to find the bounding box of non-white content,
// then returns a cropped canvas with padding around that region.
const trimWhiteSpace = (canvas) => {
  const ctx = canvas.getContext('2d')
  const { width, height } = canvas
  const { data } = ctx.getImageData(0, 0, width, height)

  const isRowWhite = (y) => {
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4
      if (
        data[i + 3] > 0 &&
        (data[i] < TRIM_THRESHOLD || data[i + 1] < TRIM_THRESHOLD || data[i + 2] < TRIM_THRESHOLD)
      ) {
        return false
      }
    }
    return true
  }

  const isColWhite = (x) => {
    for (let y = 0; y < height; y++) {
      const i = (y * width + x) * 4
      if (
        data[i + 3] > 0 &&
        (data[i] < TRIM_THRESHOLD || data[i + 1] < TRIM_THRESHOLD || data[i + 2] < TRIM_THRESHOLD)
      ) {
        return false
      }
    }
    return true
  }

  let top = 0
  let bottom = height - 1
  let left = 0
  let right = width - 1

  while (top < height && isRowWhite(top)) top++
  while (bottom > top && isRowWhite(bottom)) bottom--
  while (left < width && isColWhite(left)) left++
  while (right > left && isColWhite(right)) right--

  top = Math.max(0, top - TRIM_PADDING)
  bottom = Math.min(height - 1, bottom + TRIM_PADDING)
  left = Math.max(0, left - TRIM_PADDING)
  right = Math.min(width - 1, right + TRIM_PADDING)

  const croppedWidth = right - left + 1
  const croppedHeight = bottom - top + 1
  const cropped = document.createElement('canvas')
  cropped.width = croppedWidth
  cropped.height = croppedHeight
  cropped.getContext('2d').drawImage(canvas, left, top, croppedWidth, croppedHeight, 0, 0, croppedWidth, croppedHeight)

  return cropped
}

const useFanChartPDF = (personId) => {
  const [fanChartImageUrl, setFanChartImageUrl] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const {
    generationsCount: rawGenerationsCount,
    totalPeople,
    refetch: refetchGenerationCount,
    loading: countLoading,
  } = useGenerationCount(personId, MAX_GENERATIONS)
  const numberOfGenerations = rawGenerationsCount ? Math.max(MIN_GENERATIONS, rawGenerationsCount) : null
  const isOnlyPerson = totalPeople != null && totalPeople === 1
  const convertPDFToImage = useCallback(async (arrayBuffer) => {
    try {
      // Load the PDF document
      const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer })
      const pdf = await loadingTask.promise

      // Get the first page
      const page = await pdf.getPage(1)

      // Render at 4x scale for sharper text quality
      const viewport = page.getViewport({ scale: 4 })

      // Create canvas
      const canvas = document.createElement('canvas')
      const context = canvas.getContext('2d')
      canvas.width = viewport.width
      canvas.height = viewport.height

      // Render PDF page to canvas
      const renderContext = {
        canvasContext: context,
        viewport,
      }

      await page.render(renderContext).promise

      const trimmedCanvas = trimWhiteSpace(canvas)

      return trimmedCanvas.toDataURL('image/png')
    } catch (err) {
      throw new Error('Failed to render fan chart PDF', { cause: err })
    }
  }, [])

  const fetchAndConvertFanChart = useCallback(async () => {
    if (!personId) return

    // Wait for generation count to resolve before fetching
    if (!numberOfGenerations) return
    const cacheKey = `${personId}_${numberOfGenerations}`
    const cachedImageUrl = fanChartCache.get(cacheKey)
    if (cachedImageUrl) {
      setFanChartImageUrl(cachedImageUrl)
      setError(null)
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      // Fetch PDF from API
      const { error: fetchError, response } = await fetchFanChartPDF({
        personId,
        locale: i18n.language || 'en',
        view: 'FAMILY_LINES',
        numGenerations: numberOfGenerations,
      })

      if (fetchError) {
        throw fetchError
      }

      if (!response?.data) {
        throw new Error('No data received from fan chart API')
      }

      // Convert PDF arraybuffer to image
      const imageUrl = await convertPDFToImage(response.data)

      // Store in cache
      fanChartCache.set(`${personId}_${numberOfGenerations}`, imageUrl)

      // Update state
      setFanChartImageUrl(imageUrl)
      setError(null)
    } catch (err) {
      setError(err)
      setFanChartImageUrl(null)
    } finally {
      setIsLoading(false)
    }
  }, [personId, numberOfGenerations, convertPDFToImage])

  const refetch = useCallback(() => {
    if (personId && numberOfGenerations) {
      fanChartCache.delete(`${personId}_${numberOfGenerations}`)
    }
    refetchGenerationCount()
  }, [personId, numberOfGenerations, refetchGenerationCount])

  useEffect(() => {
    if (!countLoading) fetchAndConvertFanChart()
  }, [fetchAndConvertFanChart, countLoading])

  return {
    fanChartImageUrl,
    isLoading: isLoading || countLoading,
    error,
    refetch,
    isOnlyPerson,
  }
}

export default useFanChartPDF
